import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { Markdown } from '@client/components/_ui/display/Markdown';
import { Space } from '@client/components/_ui/layout/Space';
import { useThemeStore } from '@client/stores/themeStore';
import { createFileRoute } from '@tanstack/react-router';
import { Card, Divider, Radio, Switch, Typography } from 'antd';
import { ExternalLink, Moon, Sun } from 'lucide-react';
import { type ReactNode, useCallback, useState } from 'react';

const { Title, Text, Paragraph } = Typography;

export const Route = createFileRoute('/console/$orgId/component-showcase/markdown-showcase')({
    staticData: {
        breadcrumb: { title: 'Markdown Showcase' },
        featureFlag: 'enable-debug-info',
    },
    component: MarkdownShowcase,
});

const SAMPLE_CONTENT = {
    basic: `# Hello World

This is a **basic** markdown example with *italic* text and \`inline code\`.

- First item
- Second item
- Third item

[Learn more](https://example.com)`,

    headings: `# Heading 1
## Heading 2
### Heading 3
#### Heading 4
##### Heading 5
###### Heading 6`,

    lists: `## Lists Demo

### Unordered List
- First item
- Second item
  - Nested item
  - Another nested item
- Third item

### Ordered List
1. First step
2. Second step
3. Third step

### Task List
- [x] Completed task
- [ ] Pending task
- [ ] Another pending task`,

    code: `## Code Examples

Inline code: \`const greeting = "Hello World";\`

### JavaScript
\`\`\`javascript
function fibonacci(n) {
    if (n <= 1) return n;
    return fibonacci(n - 1) + fibonacci(n - 2);
}

console.log(fibonacci(10)); // 55
\`\`\`

### TypeScript
\`\`\`typescript
interface User {
    id: string;
    name: string;
    email: string;
}

const getUser = async (id: string): Promise<User> => {
    const response = await fetch(\`/api/users/\${id}\`);
    return response.json();
};
\`\`\``,

    tables: `## Table Example

| Feature | Status | Priority |
|---------|--------|----------|
| Authentication | Complete | High |
| Dashboard | In Progress | High |
| Reports | Pending | Medium |
| Settings | Complete | Low |`,

    blockquote: `## Blockquotes

> This is a blockquote. It can span multiple lines and is often used for quotations or callouts.
>
> — Someone Famous

Regular paragraph after the blockquote.`,

    full: `# Comprehensive Markdown Demo

This document showcases all supported markdown features.

## Text Formatting

Regular text, **bold text**, *italic text*, ***bold and italic***, and \`inline code\`.

You can also use ~~strikethrough~~ text.

## Lists

### Unordered
- Item one
- Item two
- Item three

### Ordered
1. First
2. Second
3. Third

## Links and Images

[External link](https://example.com) opens in a new tab.

## Code Block

\`\`\`typescript
interface Props {
    children: string;
    variant?: 'default' | 'document' | 'chat' | 'compact';
}
\`\`\`

## Tables

| Name | Type | Default |
|------|------|---------|
| variant | string | 'default' |
| mode | string | 'static' |
| isStreaming | boolean | false |

## Blockquote

> Information callout or quotation.

---

That's the end of the demo!`,
};

// Custom link renderer for demonstration
function DemoLinkRenderer({ href, children }: { href?: string; children?: ReactNode }) {
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-accent-500 underline hover:text-accent-600"
        >
            {children}
            <ExternalLink size={12} className="opacity-60" />
        </a>
    );
}

function useStreamingSimulation(content: string, speed: number = 20) {
    const [streamedContent, setStreamedContent] = useState('');
    const [isStreaming, setIsStreaming] = useState(false);

    const startStreaming = useCallback(() => {
        setStreamedContent('');
        setIsStreaming(true);

        let index = 0;
        const interval = setInterval(() => {
            if (index < content.length) {
                const chunkSize = Math.floor(Math.random() * 5) + 1;
                setStreamedContent((prev) => prev + content.slice(index, index + chunkSize));
                index += chunkSize;
            } else {
                setIsStreaming(false);
                clearInterval(interval);
            }
        }, speed);

        return () => clearInterval(interval);
    }, [content, speed]);

    const reset = useCallback(() => {
        setStreamedContent('');
        setIsStreaming(false);
    }, []);

    return { streamedContent, isStreaming, startStreaming, reset };
}

function VariantComparisonSection() {
    const [activeVariant, setActiveVariant] = useState<'default' | 'document' | 'chat' | 'compact'>('default');

    return (
        <Card
            title={
                <div>
                    <Title level={4} className="!mb-0">
                        Styling Variants
                    </Title>
                    <Text type="secondary" className="text-sm">
                        Compare different styling variants for various use cases
                    </Text>
                </div>
            }
            className="mb-6"
        >
            <div className="mb-4">
                <Radio.Group
                    value={activeVariant}
                    onChange={(e) => setActiveVariant(e.target.value)}
                    optionType="button"
                    buttonStyle="solid"
                >
                    <Radio.Button value="default">Default</Radio.Button>
                    <Radio.Button value="document">Document</Radio.Button>
                    <Radio.Button value="chat">Chat</Radio.Button>
                    <Radio.Button value="compact">Compact</Radio.Button>
                </Radio.Group>
            </div>

            <div className="mb-4 text-muted-foreground text-sm">
                {activeVariant === 'default' && 'Standard styling for general content with balanced spacing.'}
                {activeVariant === 'document' && 'Enhanced styling for longer documents with better heading hierarchy.'}
                {activeVariant === 'chat' && 'Compact styling optimised for chat messages and AI responses.'}
                {activeVariant === 'compact' && 'Minimal styling with reduced spacing for dense content.'}
            </div>

            <div className="rounded-lg border border-separator bg-card p-4">
                <Markdown key={activeVariant} variant={activeVariant} linkRenderer={DemoLinkRenderer}>
                    {SAMPLE_CONTENT.full}
                </Markdown>
            </div>

            <Divider />

            <div>
                <Text strong className="mb-2 block">
                    Usage
                </Text>
                <pre className="overflow-x-auto rounded bg-muted p-3 text-xs">
                    {`<Markdown variant="${activeVariant}">
  {content}
</Markdown>`}
                </pre>
            </div>
        </Card>
    );
}

function StreamingDemoSection() {
    const { streamedContent, isStreaming, startStreaming, reset } = useStreamingSimulation(SAMPLE_CONTENT.basic, 30);

    return (
        <Card
            title={
                <div>
                    <Title level={4} className="!mb-0">
                        Streaming Mode
                    </Title>
                    <Text type="secondary" className="text-sm">
                        Optimised for AI-powered streaming content with caret animation
                    </Text>
                </div>
            }
            className="mb-6"
        >
            <div className="mb-4">
                <Space>
                    <Button type="primary" onClick={startStreaming} disabled={isStreaming}>
                        Start Streaming
                    </Button>
                    <Button type="secondary" onClick={reset}>
                        Reset
                    </Button>
                </Space>
            </div>

            <div className="min-h-[200px] rounded-lg border border-separator bg-card p-4">
                <Markdown variant="chat" mode="streaming" isStreaming={isStreaming} linkRenderer={DemoLinkRenderer}>
                    {streamedContent || '_Start streaming to see the demo..._'}
                </Markdown>
            </div>

            <Divider />

            <div>
                <Text strong className="mb-2 block">
                    Usage
                </Text>
                <pre className="overflow-x-auto rounded bg-muted p-3 text-xs">
                    {`<Markdown
  mode="streaming"
  isStreaming={isGenerating}
>
  {streamingContent}
</Markdown>`}
                </pre>
            </div>
        </Card>
    );
}

function FeatureShowcaseSection() {
    const [activeFeature, setActiveFeature] = useState<'headings' | 'lists' | 'code' | 'tables' | 'blockquote'>(
        'headings',
    );

    const features = [
        { key: 'headings', label: 'Headings', content: SAMPLE_CONTENT.headings },
        { key: 'lists', label: 'Lists', content: SAMPLE_CONTENT.lists },
        { key: 'code', label: 'Code Blocks', content: SAMPLE_CONTENT.code },
        { key: 'tables', label: 'Tables', content: SAMPLE_CONTENT.tables },
        { key: 'blockquote', label: 'Blockquotes', content: SAMPLE_CONTENT.blockquote },
    ] as const;

    const activeContent = features.find((f) => f.key === activeFeature)?.content || '';

    return (
        <Card
            title={
                <div>
                    <Title level={4} className="!mb-0">
                        Markdown Features
                    </Title>
                    <Text type="secondary" className="text-sm">
                        Explore different markdown elements and their rendering
                    </Text>
                </div>
            }
            className="mb-6"
        >
            <div className="mb-4">
                <Radio.Group
                    value={activeFeature}
                    onChange={(e) => setActiveFeature(e.target.value)}
                    optionType="button"
                    buttonStyle="solid"
                >
                    {features.map((f) => (
                        <Radio.Button key={f.key} value={f.key}>
                            {f.label}
                        </Radio.Button>
                    ))}
                </Radio.Group>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <Text strong className="mb-2 block text-sm">
                        Markdown Source
                    </Text>
                    <pre className="max-h-[400px] overflow-auto rounded bg-muted p-3 text-xs">{activeContent}</pre>
                </div>
                <div>
                    <Text strong className="mb-2 block text-sm">
                        Rendered Output
                    </Text>
                    <div className="max-h-[400px] overflow-auto rounded-lg border border-separator bg-card p-4">
                        <Markdown variant="default" linkRenderer={DemoLinkRenderer}>
                            {activeContent}
                        </Markdown>
                    </div>
                </div>
            </div>
        </Card>
    );
}

function CopyButtonSection() {
    const [showCopyButton, setShowCopyButton] = useState(true);

    return (
        <Card
            title={
                <div>
                    <Title level={4} className="!mb-0">
                        Code Block Copy Button
                    </Title>
                    <Text type="secondary" className="text-sm">
                        Toggle copy button visibility for code blocks
                    </Text>
                </div>
            }
            className="mb-6"
        >
            <div className="mb-4 flex items-center gap-4">
                <Text type="secondary">Show Copy Button:</Text>
                <Switch checked={showCopyButton} onChange={setShowCopyButton} />
            </div>

            <div className="rounded-lg border border-separator bg-card p-4">
                <Markdown key={`copy-${showCopyButton}`} variant="default" showCopyButton={showCopyButton}>
                    {SAMPLE_CONTENT.code}
                </Markdown>
            </div>

            <Divider />

            <div>
                <Text strong className="mb-2 block">
                    Usage
                </Text>
                <pre className="overflow-x-auto rounded bg-muted p-3 text-xs">
                    {`<Markdown showCopyButton={${showCopyButton}}>
  {contentWithCode}
</Markdown>`}
                </pre>
            </div>
        </Card>
    );
}

function ApiReferenceSection() {
    return (
        <Card
            title={
                <div>
                    <Title level={4} className="!mb-0">
                        API Reference
                    </Title>
                    <Text type="secondary" className="text-sm">
                        Complete props documentation
                    </Text>
                </div>
            }
            className="mb-6"
        >
            <Paragraph className="mb-4 text-muted-foreground italic">
                Import from{' '}
                <code className="rounded bg-muted px-1.5 py-0.5 text-xs">@client/components/_ui/display/Markdown</code>
            </Paragraph>

            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-separator border-b">
                            <th className="py-2 pr-4 text-left font-medium">Prop</th>
                            <th className="px-4 py-2 text-left font-medium">Type</th>
                            <th className="px-4 py-2 text-left font-medium">Default</th>
                            <th className="px-4 py-2 text-left font-medium">Description</th>
                        </tr>
                    </thead>
                    <tbody className="text-muted-foreground">
                        <tr className="border-separator border-b">
                            <td className="py-2 pr-4 font-mono text-xs">children</td>
                            <td className="px-4 py-2 font-mono text-xs">string</td>
                            <td className="px-4 py-2 font-mono text-xs">required</td>
                            <td className="px-4 py-2">The markdown content to render</td>
                        </tr>
                        <tr className="border-separator border-b">
                            <td className="py-2 pr-4 font-mono text-xs">variant</td>
                            <td className="px-4 py-2 font-mono text-xs">'default' | 'document' | 'chat' | 'compact'</td>
                            <td className="px-4 py-2 font-mono text-xs">'default'</td>
                            <td className="px-4 py-2">Styling variant for different use cases</td>
                        </tr>
                        <tr className="border-separator border-b">
                            <td className="py-2 pr-4 font-mono text-xs">mode</td>
                            <td className="px-4 py-2 font-mono text-xs">'streaming' | 'static'</td>
                            <td className="px-4 py-2 font-mono text-xs">'static'</td>
                            <td className="px-4 py-2">Rendering mode - streaming for AI output</td>
                        </tr>
                        <tr className="border-separator border-b">
                            <td className="py-2 pr-4 font-mono text-xs">isStreaming</td>
                            <td className="px-4 py-2 font-mono text-xs">boolean</td>
                            <td className="px-4 py-2 font-mono text-xs">false</td>
                            <td className="px-4 py-2">Whether content is currently being streamed (shows caret)</td>
                        </tr>
                        <tr className="border-separator border-b">
                            <td className="py-2 pr-4 font-mono text-xs">caret</td>
                            <td className="px-4 py-2 font-mono text-xs">'block' | 'circle' | 'none'</td>
                            <td className="px-4 py-2 font-mono text-xs">'block'</td>
                            <td className="px-4 py-2">Caret style for streaming indicator</td>
                        </tr>
                        <tr className="border-separator border-b">
                            <td className="py-2 pr-4 font-mono text-xs">className</td>
                            <td className="px-4 py-2 font-mono text-xs">string</td>
                            <td className="px-4 py-2 font-mono text-xs">''</td>
                            <td className="px-4 py-2">Additional CSS classes for the container</td>
                        </tr>
                        <tr className="border-separator border-b">
                            <td className="py-2 pr-4 font-mono text-xs">linkRenderer</td>
                            <td className="px-4 py-2 font-mono text-xs">
                                {'(props: { href?: string; children?: ReactNode }) => ReactNode'}
                            </td>
                            <td className="px-4 py-2 font-mono text-xs">undefined</td>
                            <td className="px-4 py-2">Custom link renderer for internal navigation</td>
                        </tr>
                        <tr>
                            <td className="py-2 pr-4 font-mono text-xs">showCopyButton</td>
                            <td className="px-4 py-2 font-mono text-xs">boolean</td>
                            <td className="px-4 py-2 font-mono text-xs">true (static) / false (streaming)</td>
                            <td className="px-4 py-2">Show copy button for code blocks</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <Divider />

            <div>
                <Text strong className="mb-2 block">
                    Basic Usage
                </Text>
                <pre className="overflow-x-auto rounded bg-muted p-3 text-xs">
                    {`import { Markdown } from '@client/components/_ui/display/Markdown';

// Static content
<Markdown variant="document">
  {markdownContent}
</Markdown>

// Streaming AI content
<Markdown
  mode="streaming"
  isStreaming={isGenerating}
  caret="block"
  variant="chat"
>
  {streamingContent}
</Markdown>

// With custom link handler
<Markdown linkRenderer={ChatMarkdownLink}>
  {content}
</Markdown>`}
                </pre>
            </div>
        </Card>
    );
}

function MarkdownShowcase() {
    const { theme, setTheme } = useThemeStore();
    const isDark =
        theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

    return (
        <PageController route={Route}>
            <div className="mx-auto max-w-6xl p-6">
                <div className="mb-8 flex items-start justify-between">
                    <div>
                        <Title level={2}>Markdown Component Showcase</Title>
                        <Paragraph className="max-w-2xl text-muted-foreground">
                            Powered by Streamdown - a drop-in replacement for react-markdown, designed for AI-powered
                            streaming. Supports both streaming and static modes with multiple styling variants.
                        </Paragraph>
                    </div>
                    <div className="flex items-center gap-3 rounded-lg border border-separator bg-card px-4 py-2">
                        <Sun size={16} className={!isDark ? 'text-accent-500' : 'text-muted-foreground'} />
                        <Switch
                            checked={isDark}
                            onChange={(checked) => setTheme(checked ? 'dark' : 'light')}
                            checkedChildren={<Moon size={12} />}
                            unCheckedChildren={<Sun size={12} />}
                        />
                        <Moon size={16} className={isDark ? 'text-accent-500' : 'text-muted-foreground'} />
                    </div>
                </div>

                {/* Sections */}
                <VariantComparisonSection />

                <Divider>
                    <Text type="secondary">Streaming Demo</Text>
                </Divider>

                <StreamingDemoSection />

                <Divider>
                    <Text type="secondary">Markdown Features</Text>
                </Divider>

                <FeatureShowcaseSection />

                <Divider>
                    <Text type="secondary">Code Block Options</Text>
                </Divider>

                <CopyButtonSection />

                <Divider>
                    <Text type="secondary">API Reference</Text>
                </Divider>

                <ApiReferenceSection />
            </div>
        </PageController>
    );
}
